import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";

const DAYS = [
  [-1, "Mọi ngày"],
  [1, "Thứ hai"],
  [2, "Thứ ba"],
  [3, "Thứ tư"],
  [4, "Thứ năm"],
  [5, "Thứ sáu"],
  [6, "Thứ bảy"],
  [0, "Chủ nhật"],
];
const DAY_LABELS = Object.fromEntries(DAYS);
const MULS = [
  ["expMul", "Exp"],
  ["coinMul", "Ngọc"],
  ["dropMul", "Rơi đồ"],
  ["bossCounterMul", "Bộ đếm boss"],
];
const MAX_MUL = 10;
const EMPTY_SLOT = {
  startHour: 20,
  endHour: 22,
  dayOfWeek: -1,
  expMul: 1,
  coinMul: 1,
  dropMul: 1,
  bossCounterMul: 1,
  enable: true,
};

const hasEffect = (s) => MULS.some(([key]) => Number(s[key]) !== 1);

/** Các cặp khung cùng bật, cùng ngày (hoặc một bên mọi ngày) và giờ chồng nhau: hệ số nhân dồn. */
function overlaps(rows) {
  const on = rows.filter((r) => r.enable && hasEffect(r));
  const pairs = [];
  for (let i = 0; i < on.length; i++) {
    for (let j = i + 1; j < on.length; j++) {
      const a = on[i];
      const b = on[j];
      const sameDay = a.dayOfWeek === -1 || b.dayOfWeek === -1 || a.dayOfWeek === b.dayOfWeek;
      if (sameDay && a.startHour < b.endHour && b.startHour < a.endHour) pairs.push([a, b]);
    }
  }
  return pairs;
}

const slotLabel = (s) => `${DAY_LABELS[s.dayOfWeek]} ${s.startHour}h-${s.endHour}h`;
/** "exp x2 · rơi đồ x1.5" — chỉ các hệ số khác 1. */
const effectText = (s) =>
  MULS.filter(([key]) => Number(s[key]) !== 1)
    .map(([key, label]) => `${label.toLowerCase()} x${s[key]}`)
    .join(" · ") || "không nhân gì";

function SlotDialog({ slot, onClose, onDone }) {
  const [run, busy] = useRunner();
  const [form, setForm] = useState(() => ({ ...EMPTY_SLOT, ...slot }));
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const start = Number(form.startHour);
  const end = Number(form.endHour);
  const blocker = !(start >= 0 && start <= 23 && end >= 1 && end <= 24)
    ? "Giờ bắt đầu 0 - 23, giờ kết thúc 1 - 24."
    : end <= start
    ? "Giờ kết thúc phải sau giờ bắt đầu (khung không qua nửa đêm được — tách làm hai khung)."
    : MULS.some(([key]) => !(Number(form[key]) >= 1 && Number(form[key]) <= MAX_MUL))
    ? `Hệ số từ 1 đến ${MAX_MUL}.`
    : null;

  const submit = async () => {
    const body = {
      startHour: Number(form.startHour),
      endHour: Number(form.endHour),
      dayOfWeek: Number(form.dayOfWeek),
      expMul: Number(form.expMul),
      coinMul: Number(form.coinMul),
      dropMul: Number(form.dropMul),
      bossCounterMul: Number(form.bossCounterMul),
      enable: form.enable,
    };
    const res = await run(
      () =>
        slot.id
          ? api.put(`/api/admin/boss/golden-hours/${slot.id}`, body)
          : api.post("/api/admin/boss/golden-hours", body),
      slot.id ? "Đã lưu khung giờ vàng" : "Đã thêm khung giờ vàng"
    );
    if (res.ok) {
      onClose();
      onDone();
    }
  };

  return (
    <ActionDialog
      open
      title={slot.id ? `Sửa khung giờ vàng ${slotLabel(slot)}` : "Thêm khung giờ vàng"}
      color="warning"
      submitText="Lưu và áp dụng ngay"
      busy={busy}
      disabled={blocker !== null}
      onClose={onClose}
      onSubmit={submit}
      description="Giờ kết thúc không tính chính giờ đó: 20h - 22h là từ 20:00 đến 21:59. Hệ số 1 là không đổi."
    >
      <MDInput
        select
        label="Ngày"
        value={form.dayOfWeek}
        onChange={set("dayOfWeek")}
        SelectProps={{ sx: { height: 44 } }}
      >
        {DAYS.map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </MDInput>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Bắt đầu (giờ, 0 - 23)"
            value={form.startHour}
            inputProps={{ min: 0, max: 23 }}
            onChange={set("startHour")}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="number"
            label="Kết thúc (giờ, 1 - 24)"
            value={form.endHour}
            inputProps={{ min: 1, max: 24 }}
            onChange={set("endHour")}
          />
        </Grid>
        {MULS.map(([key, label]) => (
          <Grid item xs={6} key={key}>
            <MDInput
              fullWidth
              type="number"
              label={`${label} x (1 - ${MAX_MUL})`}
              value={form[key]}
              inputProps={{ min: 1, max: MAX_MUL, step: 0.1 }}
              onChange={set(key)}
            />
          </Grid>
        ))}
      </Grid>
      <FormControlLabel
        control={
          <Switch
            checked={form.enable}
            onChange={(e) => setForm({ ...form, enable: e.target.checked })}
          />
        }
        label="Bật"
      />
      {blocker ? (
        <Alert severity="error">{blocker}</Alert>
      ) : (
        <Alert severity={form.enable ? "warning" : "info"}>
          {DAY_LABELS[form.dayOfWeek]} {String(start).padStart(2, "0")}:00 –{" "}
          {String(end - 1).padStart(2, "0")}:59: {effectText(form)}
          {form.enable ? "." : " (đang tắt, chưa có tác dụng)."}
        </Alert>
      )}
      <MDTypography variant="caption" color="text">
        Bộ đếm boss x3 nghĩa là mỗi quái hồi sinh cộng 3 vào bộ đếm boss, boss ra nhanh gấp 3.
      </MDTypography>
    </ActionDialog>
  );
}

SlotDialog.propTypes = {
  slot: PropTypes.shape({ id: PropTypes.number }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

/** Ba trường cũ trong config/server.json, chỉ có tác dụng khi bảng golden_hour không có khung nào bật. */
function LegacyGoldenHour({ info, onSaved }) {
  const canEdit = useAuth().can(PERMISSIONS.BOSS_EDIT);
  const [run, busy] = useRunner();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (info && !form) setForm({ start: info.start, end: info.end, counter: info.counter });
  }, [info]);

  const save = async () => {
    const res = await run(
      () =>
        api.put("/api/admin/boss/golden-hour", {
          start: Number(form.start),
          end: Number(form.end),
          counter: Number(form.counter),
        }),
      "Đã lưu giờ vàng vào config/server.json"
    );
    if (res.ok) onSaved();
  };

  if (!form) return null;
  return (
    <PageCard
      title="Giờ vàng dự phòng"
      subtitle="Chỉ dùng khi bảng bên cạnh không có khung nào bật"
      color="secondary"
    >
      <MDBox
        component="form"
        display="flex"
        flexDirection="column"
        gap={2}
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        {info.usingTable ? (
          <Alert severity="warning">
            Đang có khung giờ vàng bật ở bảng bên cạnh nên cấu hình này KHÔNG có tác dụng. Nó chỉ
            dùng lại khi tắt hoặc xoá hết các khung.
          </Alert>
        ) : (
          <MDTypography variant="button" color="text">
            Chưa có khung giờ vàng nào bật nên server đang dùng cấu hình này: chỉ nhân bộ đếm boss,
            không nhân exp, ngọc, rơi đồ. Hệ số 1 là tắt.
          </MDTypography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <MDInput
              fullWidth
              type="number"
              label="Bắt đầu (giờ)"
              value={form.start}
              onChange={(e) => setForm({ ...form, start: e.target.value })}
            />
          </Grid>
          <Grid item xs={4}>
            <MDInput
              fullWidth
              type="number"
              label="Kết thúc (giờ)"
              value={form.end}
              onChange={(e) => setForm({ ...form, end: e.target.value })}
            />
          </Grid>
          <Grid item xs={4}>
            <MDInput
              fullWidth
              type="number"
              label="Hệ số (1 - 10)"
              value={form.counter}
              onChange={(e) => setForm({ ...form, counter: e.target.value })}
            />
          </Grid>
        </Grid>
        {canEdit && (
          <MDBox>
            <MDButton type="submit" variant="gradient" color="secondary" disabled={busy}>
              <Icon>save</Icon>&nbsp;Lưu
            </MDButton>
          </MDBox>
        )}
      </MDBox>
    </PageCard>
  );
}

LegacyGoldenHour.propTypes = {
  info: PropTypes.shape({
    start: PropTypes.number,
    end: PropTypes.number,
    counter: PropTypes.number,
    usingTable: PropTypes.bool,
  }).isRequired,
  onSaved: PropTypes.func.isRequired,
};

function GoldenHourTab() {
  const canEdit = useAuth().can(PERMISSIONS.BOSS_EDIT);
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const [editing, setEditing] = useState(null);
  const list = useFetch(() => api.get("/api/admin/boss/golden-hours"), [], 30000);

  const rows = list.data?.rows || [];
  const info = list.data?.info;
  const activeIds = new Set((info?.activeSlots || []).map((s) => s.id));
  const clashes = overlaps(rows);
  const reload = () => list.reload(true);

  const toggle = async (slot) => {
    const ok = await confirm({
      title: slot.enable ? "Tắt khung giờ vàng" : "Bật khung giờ vàng",
      message: slot.enable
        ? `Tắt ${slotLabel(slot)} (${effectText(slot)}).${
            activeIds.has(slot.id)
              ? "\n\nKhung này ĐANG CHẠY — người chơi mất thưởng giờ vàng ngay."
              : ""
          }`
        : `Bật ${slotLabel(slot)}: ${effectText(slot)}. Áp dụng ngay cho mọi người chơi.`,
      color: slot.enable ? "warning" : "info",
      confirmText: slot.enable ? "Tắt" : "Bật",
    });
    if (!ok) return;
    const res = await run(
      () => api.put(`/api/admin/boss/golden-hours/${slot.id}`, { ...slot, enable: !slot.enable }),
      slot.enable ? "Đã tắt khung giờ vàng" : "Đã bật khung giờ vàng"
    );
    if (res.ok) reload();
  };

  const remove = async (slot) => {
    const ok = await confirm({
      title: "Xoá khung giờ vàng",
      message: `Xoá khung ${slotLabel(slot)}?`,
      color: "error",
      confirmText: "Xoá",
    });
    if (!ok) return;
    const res = await run(() => api.del(`/api/admin/boss/golden-hours/${slot.id}`), "Đã xoá");
    if (res.ok) reload();
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={8}>
        <PageCard
          title="Khung giờ vàng"
          subtitle="Nhân exp, ngọc, rơi đồ và bộ đếm boss theo giờ. Lưu là áp dụng ngay."
          color="warning"
          noPadding
          actions={
            canEdit && (
              <MDButton size="small" color="white" onClick={() => setEditing({})}>
                <Icon>add</Icon>&nbsp;Thêm khung
              </MDButton>
            )
          }
        >
          <MDBox px={2} display="flex" flexDirection="column" gap={1}>
            {list.error && <Alert severity="error">{list.error}</Alert>}
            {list.data?.tableMissing && (
              <Alert severity="info">
                Chưa có bảng golden_hour trong cơ sở dữ liệu. Thêm khung đầu tiên sẽ tự tạo bảng.
              </Alert>
            )}
            {info &&
              (info.active ? (
                <Alert severity="warning">
                  Đang trong giờ vàng
                  {info.usingTable
                    ? `: exp x${info.expMul} · ngọc x${info.coinMul} · rơi đồ x${info.dropMul} · boss x${info.bossCounterMul}`
                    : ` (cấu hình cũ): boss x${info.bossCounterMul}`}
                </Alert>
              ) : (
                info.next && (
                  <Alert severity="info">
                    Khung tiếp theo: {info.next.day} {info.next.text}
                  </Alert>
                )
              ))}
            {clashes.map(([a, b]) => (
              <Alert severity="warning" key={`${a.id}-${b.id}`}>
                {slotLabel(a)} và {slotLabel(b)} chồng giờ nhau: trong giờ chung, hệ số NHÂN DỒN
                (hai khung exp x1.5 cho ra x2.25).
              </Alert>
            ))}
          </MDBox>
          <AdminTable
            loading={list.loading}
            rows={rows}
            rowKey="id"
            emptyText="Chưa có khung giờ vàng nào. Server đang dùng cấu hình cũ bên cạnh."
            columns={[
              {
                key: "dayOfWeek",
                label: "Ngày",
                render: (s) => DAY_LABELS[s.dayOfWeek] ?? s.dayOfWeek,
              },
              {
                key: "hours",
                label: "Giờ",
                render: (s) => (
                  <MDBox display="flex" alignItems="center" gap={1}>
                    {s.startHour}h - {s.endHour}h
                    {activeIds.has(s.id) && <Chip size="small" color="warning" label="đang chạy" />}
                  </MDBox>
                ),
              },
              ...MULS.map(([key, label]) => ({
                key,
                label,
                align: "right",
                render: (s) => (s[key] === 1 ? "-" : `x${s[key]}`),
              })),
              {
                key: "enable",
                label: "Bật",
                render: (s) => (
                  <Switch
                    size="small"
                    checked={s.enable}
                    disabled={!canEdit || busy}
                    onChange={() => toggle(s)}
                  />
                ),
              },
              canEdit && {
                key: "actions",
                label: "",
                align: "right",
                render: (s) => (
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      size="small"
                      variant="text"
                      color="warning"
                      onClick={() => setEditing(s)}
                    >
                      <Icon>edit</Icon>&nbsp;Sửa
                    </MDButton>
                    <MDButton
                      size="small"
                      variant="text"
                      color="error"
                      disabled={busy}
                      onClick={() => remove(s)}
                    >
                      <Icon>delete</Icon>&nbsp;Xoá
                    </MDButton>
                  </MDBox>
                ),
              },
            ].filter(Boolean)}
          />
        </PageCard>
      </Grid>
      <Grid item xs={12} lg={4}>
        {info && <LegacyGoldenHour info={info} onSaved={reload} />}
      </Grid>
      {editing && <SlotDialog slot={editing} onClose={() => setEditing(null)} onDone={reload} />}
    </Grid>
  );
}

export default GoldenHourTab;
